import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { RootLayout } from './components/layout/RootLayout'
import { RouteFallback } from './components/layout/RouteFallback'
import { HomePage } from './pages/HomePage'
import { NotFoundPage } from './pages/NotFoundPage'

/**
 * EVERY DATABASE-BACKED ROUTE IS CODE-SPLIT, AND HAS TO BE.
 *
 * Docs/03 budgets 180 kB of gzipped JavaScript on a first visit because the LCP
 * target is 2.5 s on a mid-range phone on mobile data, and script is what
 * delays it. Measured properly — `node scripts/check-bundle.mjs`, which follows
 * what `index.html` actually preloads — the first load was 235 kB.
 *
 * Two things were on the critical path that a visitor reading the hero never
 * needs:
 *
 *   THE COMMUNITY FEED. The four Discover routes pulled in sixteen feed-card
 *   components, the poll arithmetic, the answer thread and their hooks. That is
 *   why they were split first. ⚠️ As of 23 Sep they are not routed at all — see
 *   the block above `RetiredDiscoverPage` — so that weight is no longer in the
 *   build. Restore the split, not just the routes, if the feed comes back.
 *
 *   SUPABASE-JS, 57 kB gzipped. `DirectoryPage`, `BusinessProfilePage` and
 *   `CategoriesPage` each import a hook that imports `@avexa/api`, which
 *   imports `@supabase/supabase-js`. An eager import of ANY of them puts the
 *   whole client in the entry graph, so `index.html` modulepreloads it on the
 *   homepage.
 *
 * So the rule is now: if a route talks to the database, it is lazy.
 *
 * ⚠️ TWO PAGES LEFT THE "STAY EAGER" LIST ON 6 OCT, AND THE ARGUMENT THAT KEPT
 * THEM THERE IS WHAT CHANGED, NOT THE RULE. Superseded text, for the record:
 * ~~the purely static marketing pages (`ForIndividualsPage`,
 * `ForBusinessesPage`, `NotFoundPage`, `HomePage`) stay eager — they share
 * everything the homepage already loads, so splitting those would cost a round
 * trip and save nothing.~~
 *
 * `ForIndividualsPage` absorbed two bands moved off the shortened homepage and
 * gained the bidding and Small Jobs sections the client asked for (§ 5.1, 5.2);
 * `ForBusinessesPage` gained the bidding panel. Neither is short any more, and
 * neither shares what it reaches: `marketing/data.ts`, `FeatureGrid`,
 * `StepList`, `FunnelDiagram` and `MarketingCta` are reached from those two and
 * from four lazy pages, and from the homepage's eager half not at all. Measured:
 * keeping them eager took the first load of `/` from 167.1 kB to 171.4 kB.
 *
 * So both are `lazy()` below, and `forIndividuals.*` / `forBusinesses.*` moved
 * from `locales/en.json` into `locales/pages/` in the same change. The full
 * reasoning, including the round trip this costs on two header links, is in the
 * header of `pages/ForIndividualsPage.tsx`.
 *
 * `HomePage` and `NotFoundPage` are still eager, and still for the original
 * reason: `/` is the entry route, and the 404 has to render without a fetch.
 *
 * ─── SPLITTING ROUTES WAS NOT ENOUGH, AND COULD NOT BE ──────────────────────
 *
 * Lazy-loading all seven of those got the first load from 235 kB to 223 kB and
 * then stopped, because supabase-js was never reachable only through a route.
 * `Footer` is inside `RootLayout`, so it renders on EVERY page, and it calls
 * `useCategoryTree`; `HeroSection` calls it too. Whatever `useCategories`
 * imports is therefore eager on the homepage no matter which routes are lazy —
 * and it imported `getAvexaClient`, which statically imported the client
 * factory.
 *
 * The fix is in `lib/avexa.ts`: `getAvexaClient` is async and fetches the
 * factory with `await import('@avexa/api/client')` on first use. Its header
 * explains why that import must be the deep one and not the barrel. That is
 * what took the last 57 kB off, and it is why every `queryFn` on this site is
 * async — they were always awaiting a round trip, so the module fetch overlaps
 * work the query was doing anyway.
 *
 * First load of `/` is now 165.4 kB gzipped. `node scripts/check-bundle.mjs`
 * is the only number worth quoting; it reads what `index.html` preloads.
 *
 * SEO is unaffected: this site is client-rendered on every route (Netlify
 * serves the same `index.html` shell for `/*` — see netlify.toml), so a crawler
 * was always executing JavaScript to see `DirectoryPage`. `useDocumentMeta` and
 * the JSON-LD blocks still run, one module-fetch later.
 */
/**
 * ⚠️ THE FOUR DISCOVER ROUTES NOW RENDER ONE RETIRED PAGE (23 Sep).
 *
 * `Docs/27` § 1: *"Posting should be removed entirely for now."* On this website
 * `/discover` WAS the community feed and nothing else, so all four paths render
 * `RetiredDiscoverPage`, which explains what happened and links on. Its header
 * is the restore procedure; the four `lazy()` declarations that used to be here
 * are step 1 of it, and `DiscoverPage`, `DiscoverPostPage`, `QuestionPage` and
 * `TopicPage` are all still in `./pages` and still type-checked.
 *
 * Not routing them is also what takes their chunks — sixteen feed-card
 * components, the poll arithmetic and the answer thread — out of the build
 * entirely, which the paragraph above explains was the reason they were split
 * first. `node scripts/check-bundle.mjs` measures the first load either way:
 * these were lazy, so the headline number is unchanged and it is the total
 * shipped JavaScript that drops.
 */
const RetiredDiscoverPage = lazy(() =>
  import('./pages/RetiredDiscoverPage').then((m) => ({ default: m.RetiredDiscoverPage })),
)

/*
 * ⚠️ THE TWO AUDIENCE PAGES ARE LAZY AS OF 6 OCT. They were eager; see the
 * superseded paragraph in the block above for the argument that used to hold and
 * the measurement that ended it. Their strings live in `locales/pages/` now, not
 * in the base catalogue.
 */
const ForIndividualsPage = lazy(() =>
  import('./pages/ForIndividualsPage').then((m) => ({ default: m.ForIndividualsPage })),
)
const ForBusinessesPage = lazy(() =>
  import('./pages/ForBusinessesPage').then((m) => ({ default: m.ForBusinessesPage })),
)

const CategoriesPage = lazy(() =>
  import('./pages/CategoriesPage').then((m) => ({ default: m.CategoriesPage })),
)
const BusinessProfilePage = lazy(() =>
  import('./pages/BusinessProfilePage').then((m) => ({ default: m.BusinessProfilePage })),
)
const DirectoryPage = lazy(() =>
  import('./pages/DirectoryPage').then((m) => ({ default: m.DirectoryPage })),
)

/**
 * ─── THE COMPANY AND LEGAL ROUTES ARE LAZY TOO, FOR A DIFFERENT REASON ──────
 *
 * None of these eight talks to the database, so the rule above ("if a route
 * talks to the database, it is lazy") does not reach them. They are split on the
 * other half of the same argument: they are LONG and they are RARELY VISITED.
 *
 * The three legal documents alone are ~120 rendered blocks, and the FAQ carries
 * its own `FAQPage` JSON-LD built from every answer on the page. Eager, that
 * copy sits in the entry chunk of a homepage whose visitors overwhelmingly never
 * open a cookie policy — 165.6 kB against a 180 kB budget leaves 14.4 kB of
 * headroom, and a privacy policy is exactly the wrong thing to spend it on.
 *
 * Contrast `ForIndividualsPage`, which stays eager: it is short, it is one click
 * from the header on every page, and it shares every component the homepage has
 * already loaded, so splitting it would buy a round trip and save nothing.
 */
/**
 * ─── THE FIVE PAGES THE CLIENT SAID THE SITE WAS MISSING (22 Sep) ───────────
 *
 * `Docs/From Client/Avexa Marketplace - Feedback.docx` § 3 asks for "a more
 * complete structure … multiple dedicated pages/tabs rather than feeling like a
 * «similar web-app»", with two references: their own `Nettside - Example.pdf`
 * and marriage4life.app. The lesson from the second is not its labels, it is
 * that every one of its nineteen nav entries is a PAGE.
 *
 * All five are lazy on the second argument above — long, rarely visited, and
 * each carrying its own slice of `locales/pages`, which must not land on the
 * homepage's first load. Not one of them talks to the database, `/deals`
 * included, and `features/company/data/deals.ts` explains at length why that is
 * a decision rather than an omission.
 *
 * Two entries from the client's reference footer are deliberately NOT here:
 * "Marketing" (sponsored placement and Boost — the payment module, whose source
 * document gives two contradicting price lists, Docs/15 N20–N24) and "Business
 * Terms" (a second legal instrument, when the first three still need a
 * Norwegian lawyer). The reasoning lives next to the footer nav in
 * `lib/navigation.ts` so that it is read by whoever next wonders where they are.
 */
const HowItWorksPage = lazy(() =>
  import('./pages/HowItWorksPage').then((m) => ({ default: m.HowItWorksPage })),
)
const SafetyPage = lazy(() =>
  import('./pages/SafetyPage').then((m) => ({ default: m.SafetyPage })),
)
const ReportPage = lazy(() =>
  import('./pages/ReportPage').then((m) => ({ default: m.ReportPage })),
)
const CareersPage = lazy(() =>
  import('./pages/CareersPage').then((m) => ({ default: m.CareersPage })),
)
/**
 * 🔴 `/delete-account` IS A RELEASE BLOCKER, NOT A MARKETING PAGE.
 *
 * The path is pasted into BOTH of Google Play's Data safety URLs already, so it
 * must exist and must keep its slug — `lib/navigation.ts` has the detail. Lazy
 * like its neighbours, and for their second reason: it is long, almost entirely
 * prose, and carries its own slice of `locales/pages`, which must not land on
 * the homepage's first load.
 */
const DeleteAccountPage = lazy(() =>
  import('./pages/DeleteAccountPage').then((m) => ({ default: m.DeleteAccountPage })),
)
const DealsPage = lazy(() => import('./pages/DealsPage').then((m) => ({ default: m.DealsPage })))

const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })))
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })),
)
const FaqPage = lazy(() => import('./pages/FaqPage').then((m) => ({ default: m.FaqPage })))
const SupportPage = lazy(() =>
  import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })),
)
const DownloadPage = lazy(() =>
  import('./pages/DownloadPage').then((m) => ({ default: m.DownloadPage })),
)
const ClaimBusinessPage = lazy(() =>
  import('./pages/ClaimBusinessPage').then((m) => ({ default: m.ClaimBusinessPage })),
)
const PrivacyPage = lazy(() =>
  import('./pages/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
)
const TermsPage = lazy(() =>
  import('./pages/legal/TermsPage').then((m) => ({ default: m.TermsPage })),
)
const CookiesPage = lazy(() =>
  import('./pages/legal/CookiesPage').then((m) => ({ default: m.CookiesPage })),
)

/**
 * ─── THE LAST EIGHT ROUTES IN Docs/07, AND WHAT EACH ONE BECAME ────────────
 *
 * `ComingSoon` and `placeholderRoutes` are gone. Every route in the spec's map
 * now renders something that is true; three of them are true in a way the map
 * did not anticipate, and each says so on the page as well as in a comment.
 *
 *   /requests            BUILT. The public feed of open demand, on real data.
 *   /requests/:id        BUILT. One open request, with the customer's identity
 *                        and street address absent from the RESPONSE and not
 *                        merely from the render — see `browseOpenRequests` and
 *                        `getOpenRequest` in `packages/api/src/requests.ts`, and
 *                        `features/requests/components/RequestLocation.tsx`.
 *   /blog                BUILT, against `posts` where `kind = 'article'` rather
 *   /blog/:slug          than against the CMS tables Docs/07 imagined. The
 *                        argument is in `features/blog/hooks/useArticles.ts`:
 *                        the schema that shipped already holds articles, and
 *                        waiting for `cms_pages` was waiting for a second,
 *                        worse home for content that exists.
 *   /pricing             NOT BUILT, ON PURPOSE, and no longer vague about it.
 *                        There is no `plans` table (HTTP 404 against the live
 *                        database) and every figure is contested in the client's
 *                        own document — Docs/15 N20, N21, N22. The page names
 *                        the mechanisms, says nothing is charged today, and
 *                        shows the three blocking decisions as `ClientInput`
 *                        holes. `features/site/data.ts` has the full case.
 *   /login               NOT AN AUTH FLOW, ON PURPOSE. This site has no
 *                        authenticated area to sign in to and `lib/avexa.ts`
 *                        sets `persistSession: false` deliberately; a form here
 *                        would authenticate somebody and return them to the same
 *                        public homepage. The page answers the question the
 *                        header's "Log in" button raises. Same file, bottom.
 *   /jobs                RETIRED. "Recruitment should NOT be included" —
 *   /jobs/:id            `Feedback from Hassan.docx` § 29, after the 21 Sep
 *                        rejection (Docs/22 § 3). The whole product-wide
 *                        retirement is Docs/23 § 11b. The routes stay so old
 *                        links explain themselves instead of 404-ing.
 *                        `RetiredJobsPage` has the reasoning and the way back.
 *
 * All eight are lazy. `/requests` and `/blog` talk to the database, so the rule
 * at the top of this file reaches them; the other four are split on the second
 * argument — long, rarely visited, and carrying their own slice of
 * `locales/pages`, which must not land on the homepage's first load.
 */
const RequestsPage = lazy(() =>
  import('./pages/RequestsPage').then((m) => ({ default: m.RequestsPage })),
)
const RequestDetailPage = lazy(() =>
  import('./pages/RequestDetailPage').then((m) => ({ default: m.RequestDetailPage })),
)
const BlogPage = lazy(() => import('./pages/BlogPage').then((m) => ({ default: m.BlogPage })))
const BlogPostPage = lazy(() =>
  import('./pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage })),
)
const PricingPage = lazy(() =>
  import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })),
)
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })))
const BillingPage = lazy(() =>
  import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })),
)
const RetiredJobsPage = lazy(() =>
  import('./pages/RetiredJobsPage').then((m) => ({ default: m.RetiredJobsPage })),
)

export const App = () => (
  <Routes>
    <Route element={<RootLayout />}>
      <Route index element={<HomePage />} />

      {/* Live, database-backed routes. */}
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/business/:slug" element={<BusinessProfilePage />} />

      {/*
        Discover — RETIRED FOR NOW, 23 Sep. All four paths render one page.

        They were the community feed (`/discover`, whose tab was a query
        parameter rather than a path segment) and the long-tail SEO surface under
        it: `/q/:id` a community question, addressed by id because it has no
        slug; `/p/:slug` a post, which takes either a slug or an id; and
        `/topic/:slug` a topic. Every one of them was posting, so every one of
        them goes, and every one of them STAYS ROUTED so an indexed URL or an old
        link explains itself instead of hitting the generic 404.

        `RetiredDiscoverPage` sets `noIndex` and keeps its outbound links
        crawlable, which is what moves the accumulated value of these URLs to the
        pages that survived.
      */}
      <Route
        path="/discover"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RetiredDiscoverPage />
          </Suspense>
        }
      />
      <Route
        path="/discover/q/:id"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RetiredDiscoverPage />
          </Suspense>
        }
      />
      <Route
        path="/discover/p/:slug"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RetiredDiscoverPage />
          </Suspense>
        }
      />
      <Route
        path="/discover/topic/:slug"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RetiredDiscoverPage />
          </Suspense>
        }
      />

      {/* Built marketing routes. Content is still code until `cms_sections` lands. */}
      <Route
        path="/for-individuals"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ForIndividualsPage />
          </Suspense>
        }
      />
      <Route
        path="/for-businesses"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ForBusinessesPage />
          </Suspense>
        }
      />

      {/*
        The whole loop, both directions through it, and the half that happens
        after an offer is accepted. The client's own reference document opens
        with this section and the site had nowhere to put it.
      */}
      <Route
        path="/how-it-works"
        element={
          <Suspense fallback={<RouteFallback />}>
            <HowItWorksPage />
          </Suspense>
        }
      />

      {/*
        Trust and its limits. `/safety` describes the listed → claimed →
        verified model the client drew (Docs/15 N15) WITHOUT rendering a
        per-business badge, because claim state is inferred from
        `business_members` and RLS answers a browser "no" for every business.
        `/report` sends people to the mobile app, where reporting and blocking
        actually work, and says plainly that no response time is promised
        because nobody is rostered to read the queue.
      */}
      <Route
        path="/safety"
        element={
          <Suspense fallback={<RouteFallback />}>
            <SafetyPage />
          </Suspense>
        }
      />
      <Route
        path="/report"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ReportPage />
          </Suspense>
        }
      />

      {/*
        🔴 BOTH of Google Play's Data safety URLs point here — account deletion
        and partial data deletion. ⚠️ DELIBERATELY NOT GATED on
        `showAppSurfaces()`: this is a legal and store obligation that has to
        answer in every state of `lib/appSurfaces.ts`, unlike the nineteen
        marketing funnels behind that switch. It is indexable for the same
        reason the legal documents stopped being `noIndex` — a reviewer is sent
        here, and "excluded from search" is the wrong posture for it.
      */}
      <Route
        path="/delete-account"
        element={
          <Suspense fallback={<RouteFallback />}>
            <DeleteAccountPage />
          </Suspense>
        }
      />

      {/* {{company}}'s, not the product's. No open roles, and it says so. */}
      <Route
        path="/careers"
        element={
          <Suspense fallback={<RouteFallback />}>
            <CareersPage />
          </Suspense>
        }
      />

      {/* An explainer. One promotion row exists and nothing can claim it. */}
      <Route
        path="/deals"
        element={
          <Suspense fallback={<RouteFallback />}>
            <DealsPage />
          </Suspense>
        }
      />

      {/*
        ⚠️ `/contact` IS NOW A REAL PAGE. IT WAS A REDIRECT TO `/support` UNTIL
        6 OCT, AND THE REDIRECT'S REASONING STILL SHAPES THE PAGE.

        Superseded text, for the record: ~~`/contact` IS AN ALIAS, NOT A PAGE.
        The client's reference footer says "Contact"; this site had already built
        "Support". They are one job — find an answer, report something, or reach
        a person — and two pages doing it would each have to admit separately
        that the mailbox does not exist yet (Docs/15 Q7). So `/support` keeps the
        content and the canonical URL, and this exists so that the word a
        visitor types never lands on the 404 page.~~

        The client asked for the page directly in his 29 Sep review § 7 — *"The
        following pages should be added: Home. Contact Us."* — so the alias
        became a page. The old objection was sound, and the answer to it is that
        `/contact` owns NO content: it is a switchboard whose first card hands
        the visitor to `/support`, which keeps the help centre. `nav.support` was
        relabelled from "Contact & support" to "Help & support" in the same
        change so that two nav entries do not both say "contact".

        `features/company/data/contact.ts` has the full division of labour and
        the two reasons there is still no form and no printed address.
      */}
      <Route
        path="/contact"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ContactPage />
          </Suspense>
        }
      />

      {/*
        Company pages. Every destination in `companyNav` — the footer column
        that used to be four links to the same placeholder.
      */}
      <Route
        path="/about"
        element={
          <Suspense fallback={<RouteFallback />}>
            <AboutPage />
          </Suspense>
        }
      />
      <Route
        path="/faq"
        element={
          <Suspense fallback={<RouteFallback />}>
            <FaqPage />
          </Suspense>
        }
      />
      <Route
        path="/support"
        element={
          <Suspense fallback={<RouteFallback />}>
            <SupportPage />
          </Suspense>
        }
      />
      <Route
        path="/download"
        element={
          <Suspense fallback={<RouteFallback />}>
            <DownloadPage />
          </Suspense>
        }
      />
      <Route
        path="/claim-business"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ClaimBusinessPage />
          </Suspense>
        }
      />

      {/*
        Legal. All three are UNREVIEWED DRAFTS and all three are `noIndex` —
        see `features/legal/components/LegalPage.tsx` for why that matters more
        than it looks. They keep the `/legal/` prefix from Docs/07's route map.
      */}
      <Route
        path="/legal/privacy"
        element={
          <Suspense fallback={<RouteFallback />}>
            <PrivacyPage />
          </Suspense>
        }
      />
      <Route
        path="/legal/terms"
        element={
          <Suspense fallback={<RouteFallback />}>
            <TermsPage />
          </Suspense>
        }
      />
      <Route
        path="/legal/cookies"
        element={
          <Suspense fallback={<RouteFallback />}>
            <CookiesPage />
          </Suspense>
        }
      />

      {/*
        Open demand. `/requests` is the supply-side acquisition page and the one
        route on this site that gains a new indexable URL every time a customer
        posts a job — see `RequestsPage`. Read `RequestLocation` before touching
        anything that renders where a request is.
      */}
      <Route
        path="/requests"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RequestsPage />
          </Suspense>
        }
      />
      <Route
        path="/requests/:id"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RequestDetailPage />
          </Suspense>
        }
      />

      {/* Articles, from `posts` where `kind = 'article'`. */}
      <Route
        path="/blog"
        element={
          <Suspense fallback={<RouteFallback />}>
            <BlogPage />
          </Suspense>
        }
      />
      <Route
        path="/blog/:slug"
        element={
          <Suspense fallback={<RouteFallback />}>
            <BlogPostPage />
          </Suspense>
        }
      />

      {/* Both `noIndex`, for reasons their own headers give. */}
      <Route
        path="/pricing"
        element={
          <Suspense fallback={<RouteFallback />}>
            <PricingPage />
          </Suspense>
        }
      />
      <Route
        path="/login"
        element={
          <Suspense fallback={<RouteFallback />}>
            <LoginPage />
          </Suspense>
        }
      />
      {/* The one signed-in page. Not behind `showAppSurfaces()`: a business must be
          able to pay whatever state the marketing sections are in. */}
      <Route
        path="/billing"
        element={
          <Suspense fallback={<RouteFallback />}>
            <BillingPage />
          </Suspense>
        }
      />

      {/*
        Retired. Both render the same page: there is no detail to differ on when
        the feature does not exist. They are kept rather than deleted so a link
        from the spec, an email or an old sitemap explains itself.
      */}
      <Route
        path="/jobs"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RetiredJobsPage />
          </Suspense>
        }
      />
      <Route
        path="/jobs/:id"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RetiredJobsPage />
          </Suspense>
        }
      />

      {/*
        SEO directory landing — the commercial heart of the site.
        Declared last so every static route above wins the match; React Router
        ranks static segments above dynamic ones, but the ordering makes the
        intent obvious to the next reader.
      */}
      <Route path="/:locale/:city/:category" element={<DirectoryPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
)
